import { computed, type ComputedRef } from "vue";
import { useSites } from "./listSites";
import { useCurrentSite } from "./useCurrentSite";
import { type ColumnDefinition } from "@/types/table";

/**
 * A "Site" column that only appears when "All sites" is selected — when a
 * single site is chosen it's redundant, so it's dropped. Resolves each row's
 * site id to a name from the cached sites list. Spread `siteColumns.value` into
 * a table's (computed) columns wherever the Site column should sit.
 */
export function useSiteColumn<T>(
  getSiteId: (row: T) => string | null | undefined,
): { siteColumns: ComputedRef<ColumnDefinition<T>[]> } {
  const { data: sites } = useSites();
  const { id: currentSiteId } = useCurrentSite();

  const siteMap = computed(() => {
    const map = new Map<string, string>();
    sites.value?.forEach((site) => map.set(site.id, site.name));
    return map;
  });

  const siteColumns = computed<ColumnDefinition<T>[]>(() => {
    if (currentSiteId.value != null) return [];
    return [
      {
        label: "Site",
        name: "site",
        getValue: (row: T) => {
          const id = getSiteId(row);
          return id ? siteMap.value.get(id) ?? null : null;
        },
        autoWidth: true,
      },
    ];
  });

  return { siteColumns };
}
